import { Toaster } from "@iec-id/ui";
import { QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink } from "@trpc/client";
import { type ReactNode, useState } from "react";
import { I18nextProvider, useTranslation } from "react-i18next";
import { i18n } from "@/lib/i18n";
import { createQueryClient } from "@/lib/query-client";
import { api } from "@/lib/trpc";

/**
 * Корневые провайдеры витрины. `credentials: "include"` обязателен —
 * сессия живёт в cookie на `*.iec.exchange`, без него все запросы анонимны.
 */
export function AppProviders({ children }: { children: ReactNode }) {
	const [queryClient] = useState(createQueryClient);
	const [trpcClient] = useState(() =>
		api.createClient({
			links: [
				httpBatchLink({
					url: `${import.meta.env.VITE_API_URL ?? ""}/trpc`,
					// Батч уходит POST'ом, а не GET'ом: витрина открывает раздел десятком
					// запросов сразу, и склеенный query-string упирается в лимит длины URL —
					// прокси отвечает 414, запросы молча не доезжают (проверено на стенде).
					methodOverride: "POST",
					fetch: (input, init) => fetch(input, { ...init, credentials: "include" }),
				}),
			],
		}),
	);

	return (
		<I18nextProvider i18n={i18n}>
			<api.Provider client={trpcClient} queryClient={queryClient}>
				<QueryClientProvider client={queryClient}>
					{children}
					<AppToaster />
				</QueryClientProvider>
			</api.Provider>
		</I18nextProvider>
	);
}

/**
 * Очередь тостов кита. Подпись крестика регистрируется здесь, а не в каждом
 * вызове `notify.*`: те живут в обработчиках мутаций, где до `t()` не дотянуться,
 * а без подписи кит крестик не рисует вовсе (`@iec-id/ui` без текстовых
 * дефолтов — Д11).
 */
function AppToaster() {
	const { t } = useTranslation("common");
	return <Toaster position="bottom-right" closeLabel={t("action.close")} />;
}
